// Copyright 2025 Sorama B.V.
import { DialogController } from "aurelia-dialog";
import { autoinject } from "aurelia-framework";
import { ProgressIndicator } from "libraries/ProgressIndicator/ProgressIndicator";
import { FirmwareRepository } from "repositories/FirmwareRepository";
import { CamIv64Device } from "services/Hardware/CamHTTP/CamIV64Helpers";
import { FirmwareInfo, ICamApi } from "services/Hardware/CamHTTP/ICamApi";
import { BinaryData } from "units/Data";

export interface FirmwareUpdateModel {
    device?: CamIv64Device;
    installedVersion?: FirmwareInfo;
    remoteVersion?: string;
}

@autoinject
export class FirmwareUpdateDialog {
    // This dialog's model
    private model: FirmwareUpdateModel = {};

    // Update state properties used by the view
    protected isUpdating = false;
    protected isComplete = false;
    protected hasError = false;
    protected errorMessage = "";

    // Progress tracking properties used by the view
    protected progressText = "";
    protected progressCurrentValue: number | null = null;
    protected progressTotalValue: number | null = null;
    protected progressUnit = "MB";

    constructor(
        private readonly controller: DialogController,
        private readonly camApi: ICamApi,
        private readonly firmwareRepository: FirmwareRepository
    ) {}

    public activate(firmwareUpdateModel: FirmwareUpdateModel) {
        // Set the model directly since we no longer have default text properties
        this.model = firmwareUpdateModel;

        // Make sure we have all required properties
        if (!this.model.device) {
            throw new Error("Device is required for firmware update");
        }
        if (!this.model.installedVersion) {
            throw new Error("Installed version is required for firmware update");
        }
        if (!this.model.remoteVersion) {
            throw new Error("Remote version is required for firmware update");
        }
    }

    protected cancel(): void {
        this.controller.cancel();
    }

    protected async updateFirmware(): Promise<void> {
        // Prevent multiple concurrent updates
        if (this.isUpdating) {
            return;
        }

        try {
            this.isUpdating = true;
            this.hasError = false;
            this.errorMessage = "";
            this.progressText = "Starting firmware update...";
            this.progressCurrentValue = null;
            this.progressTotalValue = null;

            if (!this.model.device || !this.model.remoteVersion) {
                throw new Error("Missing device or firmware version information.");
            }

            if (!this.model.device.type) {
                throw new Error("Unknown device type. Please use a supported device.");
            }
            // Download firmware from repository with progress tracking
            this.progressText = "Downloading firmware...";
            let firmwareBlob: Blob;
            try {
                firmwareBlob = await this.firmwareRepository.downloadFirmware(
                    this.model.device.type,
                    this.model.remoteVersion,
                    this.handleDataProgress.bind(this)
                );
            } catch (error) {
                throw new Error(`Failed to download firmware${error instanceof Error ? ": " + error.message : "."}`);
            }

            // Upload firmware to device with progress tracking
            this.progressText = "Uploading firmware to device...";
            try {
                await this.camApi.updateDeviceFirmware(firmwareBlob, this.handleDataProgress.bind(this));
            } catch (error) {
                throw new Error(
                    `Failed to upload firmware to device${error instanceof Error ? ": " + error.message : "."}`
                );
            }

            this.isComplete = true;

            // Give user time to read the success message before closing
            setTimeout(() => {
                this.controller.ok(true);
            }, 3000); // 3 seconds delay
        } catch (e) {
            this.errorMessage =
                "Something went wrong when updating the firmware. Please try again, or contact Sorama if the problem persists.";

            console.error("Error during firmware update: ", e);

            this.hasError = true;
        } finally {
            this.isUpdating = false;
        }
    }

    /** Takes a ProgressIndicator and updates the progress tracking properties */
    private handleDataProgress(progress: ProgressIndicator): void {
        this.progressCurrentValue = progress.progressEvent.loaded / BinaryData.mebibyte.conversion;
        this.progressTotalValue = progress.progressEvent.total / BinaryData.mebibyte.conversion;
    }

    protected get updateMessage(): string {
        if (!this.model.installedVersion || !this.model.remoteVersion) {
            return "Version information not available.";
        }

        if (this.isComplete) {
            return "Firmware update completed successfully. The device has been updated to the latest version.";
        }

        if (this.isUpdating) {
            return "Firmware update in progress. Do not turn off the device.";
        }

        return `Are you sure you want to update the firmware from version ${this.model.installedVersion.version} to ${this.model.remoteVersion}?`;
    }

    protected get progressPercentage(): number {
        return (this.progressCurrentValue / this.progressTotalValue) * 100 || 0;
    }
}
